import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FAQ.css';

const FAQ_SECTIONS = [
  {
    id: 'timeline',
    title: 'Timeline & process',
    items: [
      {
        q: 'How long does a website take?',
        a: "Timelines run by tier. Starter is 3 weeks, Business is 5 weeks, Practice is 6 weeks, and Commerce is 8 weeks. All from content handoff. Smaller projects move faster, larger builds take longer because there's genuinely more to build.",
      },
      {
        q: 'How involved do I need to be?',
        a: "You don't need to know anything technical. We'll guide you through what we need, your services, preferences, and any examples you like. You'll receive previews and checkpoints along the way.",
      },
      {
        q: 'Will I be kept updated while you build?',
        a: "Yes. You'll get a progress update every week or two showing what's done and what's coming next, so you're never left guessing. These keep you informed, they aren't open revision rounds. Feedback and changes happen at the review points built into your project scope, which keeps the build on schedule and the agreed scope intact.",
      },
      {
        q: 'Can we adjust things as we go?',
        a: 'Yes. Each project includes revision rounds so we can refine layout, wording, and visuals until everything feels right while still keeping the project on track.',
      },
      {
        q: "What if I don't like the design?",
        a: "Each package includes structured revision rounds (2 to 5 depending on tier), and we use them. If something isn't working at draft stage, we say so and adjust. If you want changes beyond the included rounds, we quote them transparently before doing the work, never after.",
      },
    ],
  },
  {
    id: 'content',
    title: 'Content & preparation',
    items: [
      {
        q: 'What do I need before we start?',
        a: "The more you can bring, the faster things move, but you don't need to have everything polished. If you have a general sense of your services, pricing, and any existing branding, that's enough to get started. We handle the rest.",
      },
      {
        q: 'Do you help with writing?',
        a: 'Yes. Copywriting is included in every package. Bring your ideas, rough notes, or even just a voice memo, and we shape it into clear, on-brand copy. You review and approve before anything goes live.',
      },
      {
        q: 'Can you work with my existing brand or site?',
        a: "Absolutely. We can refresh an existing website, work within your current branding, or gradually evolve it into something that feels more aligned with where you're heading.",
      },
      {
        q: 'Why a custom-built site instead of WordPress, Wix, or Squarespace?',
        a: "Template builders like Wix and Squarespace are great if you want to build it yourself. A custom site is built around how your business actually works, loads faster (which matters for both feel and search ranking), and has no monthly platform fee. The tradeoff: you can't drag and drop pages around yourself. If that matters to you, a template builder is the right call.",
      },
    ],
  },
  {
    id: 'booking',
    title: 'Booking & payments',
    items: [
      {
        q: 'How does booking a project work?',
        a: "Start by sending a short message through the contact form with a little about your business and what you're looking for. We'll recommend a package or custom scope, share a timeline, and provide a quote.",
      },
      {
        q: 'Do you work with businesses outside New York?',
        a: "Yes. Layer & Co. is based in New York, but the work happens entirely online, so location isn't a barrier. We work with small businesses wherever they are, and being remote doesn't change the process or the pricing.",
      },
      {
        q: 'What are the payment terms?',
        a: "It depends on the size of the project. For Starter and Business, it's 50% to reserve your spot and start, and 50% before launch. For Practice and Commerce, which are larger builds, payment is split across milestones: 40% to start, 40% at the project midpoint, and 20% before launch. Either way, the exact schedule is written into your quote before anything begins.",
      },
      {
        q: 'How can I pay?',
        a: "We invoice via PayPal. You'll receive a link with a clear due date and can pay by card or PayPal balance. No account required.",
      },
      {
        q: 'What ongoing costs should I expect after launch?',
        a: "Your domain renewal, which is typically a low yearly cost. That's it. Hosting is free for normal small business traffic. If you want content updates handled for you, the maintenance plan is optional and starts at $75/month.",
      },
    ],
  },
  {
    id: 'after-launch',
    title: 'After launch',
    items: [
      {
        q: 'What happens after launch?',
        a: "You'll receive a full walkthrough of your site and documentation covering how to manage the basics. Monthly maintenance plans are available if you want ongoing support.",
      },
      {
        q: 'Do you offer ongoing maintenance?',
        a: 'Yes. Maintenance plans start at $75/month and cover content updates, small tweaks, and a monthly site check. All plans are month-to-month with no commitment. Cancel anytime.',
      },
      {
        q: 'What if I just need occasional help, not a monthly plan?',
        a: "No problem. Ad-hoc work is billed at $120/hour with a 1-hour minimum, invoiced after the work is complete. Works well for one-off updates, small fixes, or occasional changes.",
      },
      {
        q: 'Do I own my website after launch?',
        a: 'Yes. Domain, hosting, source code. All yours. No platform lock-in. No ongoing fee required just to keep your site accessible.',
      },
    ],
  },
];

const PROCESS_STEPS = [
  {
    title: 'Inquiry',
    body: "Fill out the contact form with your project details and availability. We'll reach out within 48 hours to discuss your goals, your business, and what you're trying to accomplish. No pressure, no obligation. Just a conversation.",
  },
  {
    title: 'Scope & quote',
    body: "A written proposal within 48 hours of our conversation. It covers every page, every feature, and every deliverable. Nothing vague. You'll see exactly what's included, what's out of scope, and what it costs. Payment terms are spelled out here too, scaled to the size of the project.",
  },
  {
    title: 'Design & build',
    body: "We start with structure before visuals, getting the layout and content flow right before any design decisions. You'll see a working draft within the first week, not at the end. Each package includes structured revision rounds so feedback is collected and applied in clear stages, not drip-fed over email. Timelines run 3 to 8 weeks from content handoff depending on tier.",
  },
  {
    title: 'Launch & handoff',
    body: "We handle DNS, SSL, and going live. After launch you get a walkthrough of how everything works, how to update content, what your hosting setup looks like, and who to call if something breaks. You leave owning the site completely, with no dependency on us to keep it running.",
  },
];

function FAQ() {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <main className="faq-page">

      {/* HEADER */}
      <section className="fq-header">
        <div className="fq-container">
          <div className="fq-header-row">
            <div>
              <p className="fq-eyebrow">Frequently asked</p>
              <h1>Quick answers,<br /><em>honest</em> ones.</h1>
              <p className="fq-lede">
                Common questions about timeline, content, payment, and what to expect after launch.
                Based in New York, Layer &amp; Co. builds custom websites for small businesses remotely, wherever you are.
                If something isn&apos;t covered here, get in touch and we&apos;ll answer directly.
              </p>
            </div>
          </div>
          <nav className="fq-nav" aria-label="FAQ sections">
            <span className="fq-nav-label">Jump to</span>
            <a href="#process" className="fq-nav-link">The process</a>
            {FAQ_SECTIONS.map((section) => (
              <a key={section.id} href={`#${section.id}`} className="fq-nav-link">
                {section.title}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="fq-process">
        <div className="fq-container">
          <div className="fq-section-header">
            <div>
              <p className="fq-section-eyebrow">The process</p>
              <h2>What working <em>together</em> looks like.</h2>
            </div>
            <p className="fq-section-header-blurb">
              Four steps from your first message to handing over the keys.
              No surprises, no scope creep, no vendor lock-in at the end.
            </p>
          </div>
          <div className="fq-process-grid">
            {PROCESS_STEPS.map((s, i) => (
              <div key={s.title} className="fq-process-step">
                <div className="fq-process-step-num">
                  <span>{String(i + 1).padStart(2, '0')}</span>
                  <span>step</span>
                </div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
          <div className="fq-process-cta">
            <p className="fq-process-cta-text">
              Step one is a quick message. <em>No pressure, no obligation.</em>
            </p>
            <Link to="/contact" className="fq-btn fq-btn-secondary">
              Get in touch
            </Link>
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      {FAQ_SECTIONS.map((section, sIdx) => (
        <section key={section.id} id={section.id} data-tone={['teal','warm','sage','teal'][sIdx]} className="fq-section">
          <div className="fq-container">
            <div className="fq-section-head">
              <div>
                <p className="fq-section-eyebrow">{section.title}</p>
              </div>
              <span className="fq-section-count">{String(section.items.length).padStart(2, '0')} questions</span>
            </div>
            <div className="fq-list">
              {section.items.map((item, i) => {
                const id = `${section.id}-${i}`;
                const isOpen = openId === id;
                return (
                  <div key={id} data-tone={['teal','warm','sage','teal'][sIdx]} className={`fq-item ${isOpen ? 'is-open' : ''}`}>
                    <button
                      type="button"
                      className="fq-item-trigger"
                      onClick={() => toggle(id)}
                      aria-expanded={isOpen}
                      aria-controls={`${id}-panel`}
                    >
                      <span className="fq-item-q">{item.q}</span>
                      <span className="fq-item-plus" aria-hidden="true">
                        <span className="fq-item-plus-bar fq-item-plus-bar-h" />
                        <span className="fq-item-plus-bar fq-item-plus-bar-v" />
                      </span>
                    </button>
                    <div
                      id={`${id}-panel`}
                      className="fq-item-panel"
                      role="region"
                    >
                      <div className="fq-item-panel-inner">
                        <p className="fq-item-a">{item.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* FINAL CTA */}
      <section className="fq-cta">
        <div className="fq-cta-grid" aria-hidden="true" />
        <div className="fq-cta-glow" aria-hidden="true" />
        <div className="fq-container-narrow fq-cta-inner">
          <h2>Still have <em>questions?</em></h2>
          <div className="fq-cta-actions">
            <Link to="/contact" className="fq-btn fq-btn-primary">
              Ask a question
            </Link>
            <Link to="/services" className="fq-btn fq-btn-secondary">
              View services
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}

export default FAQ;